<template>
  <div
    class="tabs"
    :class="{
      'darken': theme === 'darken',
      'lighten': theme === 'lighten',
      'size-xs': size === 'xs',
      'size-s': size === 's',
      'size-m': size === 'm',
      'size-l': size === 'l',
    }"
  >
    <div class="tabs__controls">
      <div
        v-swiper:slider="{
          slidesPerView: 'auto',
          centerInsufficientSlides: true,
          watchOverflow: true,
          resizeObserver: true,
          preventClicks: true,
          preventClicksPropagation: false,
          threshold: 12,
        }"
        :class="{ 'tabs__buttons swiper': tabs.length > 1 }"
      >
        <div
          class="tabs__wrapper swiper-wrapper"
          :class="{ 'soon': soon }"
        >
          <div
            v-for="(tab, index) in tabs"
            :key="tab.id"
            :class="{
              'tabs__item': tabs.length > 1,
              'active': activeTab.id === tab.id,
              'disabled': tab.disabled,
            }"
            :data-id="tab.dataId"
            :data-gtm-id="tab.dataGtmId"
            class="swiper-slide"
            @click="selectByTabOrId(tab, index)"
          >
            <SvgIcon
              v-if="tab.icon"
              :name="tab.icon"
              class="tabs__icon"
              :style="tab.iconColor ? { color: `var(${tab.iconColor})`} : {}"
            />
            <UiText
              :font="c_sizeButton"
              :text="tab.title"
              class="tabs__text"
            />

            <div v-if="tab.amount" class="tabs__amount">
              <UiText
                font="promo-heading-4"
                color="typography-title02"
                :text="tab.amount"
              />
            </div>
            <UiBadge
              v-if="soon"
              class="tabs__soon"
              text="soon"
            />
          </div>
        </div>
      </div>
    </div>
    <!--No body for tab like filters-->
    <Transition name="fade" mode="out-in">
      <div
        v-if="$scopedSlots[activeTab.id] || $slots[activeTab.id] || $slots.default"
        :key="activeTab.id"
        class="tabs__content"
      >
        <slot name="default" />
        <slot :name="activeTab.id" :tab="activeTab" />
      </div>
    </Transition>
  </div>
</template>
<script>
import { isBoolean, isString, isHas } from '~/util/validators/baseComponentsValidators'
import { scrollToUnvisibleSlide } from '~/util/helpers/sliderHelper'

const baseTabsTabsValidator = (value) => {
  return value.every(tab => isHas(tab, 'id') && isString(tab.id) && isHas(tab, 'title') && isString(tab.title))
}

const FILTER = 'filter'
const TAB = 'tab'
const size = ['xs', 's', 'm', 'l']

/** TODO: add scroll to selected on mount, css gradient in the end for smooth appearance */
export default {
  name: 'UiTabs',
  props: {
    tabs: { type: Array, required: true, validator: baseTabsTabsValidator }, // [{id: '', title: '', icon?: '', amount?: 1, dataGtmId?: GTM.ID. }]
    headerJustifyCenter: { type: Boolean, default: true },
    value: { type: String },
    emitObject: { type: Boolean, default: false },
    query: { type: [Boolean, String], default: true }, // false - to off, String for custom query key 'some_key'
    filter: { type: Boolean, default: false }, // to change query to filter prefix
    soon: { type: Boolean },
    disabled: { type: Boolean },
    theme: { type: String, default: 'darken' },
    size: { type: String, default: 'm', validator: (v) => size.includes(v) },
  },
  data() {
    return {
      activeTab: {},
    }
  },
  computed: {
    c_query() {
      if (isBoolean(this.query)) {
        return this.filter ? FILTER : TAB
      }
      else {
        return `${this.filter ? FILTER : TAB}_${this.query}`
      }
    },
    noQueryTabId() {
      return this.tabs[0].id
    },
    c_sizeButton() {
      if (this.size === 'm') return 'components-button-medium'
      if (this.size === 's') return 'components-button-small'
      if (this.size === 'xs') return 'components-button-micro'
      return 'components-button-large'
    },
  },
  watch: {
    value() {
      this.activeTab = this.getTabById(this.value)
    },
  },
  created() {
    this.initTabs()
  },
  methods: {
    initTabs() {
      if (this.initTabsByQuery()) return
      this.activeTab = this.value
        ? this.getTabById(this.value) || this.tabs[0]
        : this.tabs[0]
    },
    selectByTabOrId(tabOrId, index) {
      if (this.disabled) return
      const oldVal = this.activeTab
      this.activeTab = isString(tabOrId)
        ? this.getTabById(tabOrId)
        : tabOrId
      if (oldVal.id === this.activeTab.id) return
      this.emitData()
      this.updateQuery()
      scrollToUnvisibleSlide(index, this.slider)
    },
    updateQuery() {
      if (!this.query) return
      if (this.activeTab.id === this.noQueryTabId) {
        const query = { ...this.$route.query }
        delete query[this.c_query]
        this.$router.replace({ query })
      }
      else {
        if (this.$route.query[this.c_query] === this.activeTab.id) return
        this.$router.replace({
          query: {
            ...this.$route.query,
            [this.c_query]: this.activeTab.id,
          },
        })
      }
    },
    initTabsByQuery() {
      if (!this.query) return false
      if (this.$route.query.hasOwnProperty(this.c_query)) {
        this.selectByTabOrId(this.$route.query[this.c_query])
        return true
      }
      return false
    },
    getTabById(id) {
      return this.tabs.find(tab => tab.id === id) || this.tabs[0]
    },
    emitData() {
      if (this.emitObject) {
        this.$emit('input', this.activeTab)
      }
      else {
        this.$emit('input', this.activeTab.id)
      }
    },
  },
}
</script>

<style lang="scss" src="./style.scss" scoped></style>
